import { Assert } from "../../Assert";
import { FomanticShim, PartialNullable } from "../../Extensions";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, JNative } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean } from "../../RadObject";
import { ApplicationInformationService } from "../../RadWindow";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { ServiceManager } from "../../ServiceManager";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { CheckBoxTypeEnum } from "./CheckBox";
import { IInputListControl, InputListControl, InputListControlPropertyValues } from "./InputListControl";
import { NAMESPACE } from "./_Namespace";

import "./CheckBoxList.scss";

export type CheckBoxListPropertyValues =
    InputListControlPropertyValues
    & {
        checkBoxType: CheckBoxTypeEnum;
        horizontal: boolean;
    };

export type CheckBoxListProperties = {
    [name in keyof CheckBoxListPropertyValues]: Property<CheckBoxListPropertyValues[name]>
};

export interface ICheckBoxList extends IInputListControl, CheckBoxListProperties
{
}

export class CheckBoxList extends InputListControl implements ICheckBoxList
{
    // #region Static

    public static override classNamespace = NAMESPACE + "CheckBoxList";
    public static override className = "CheckBoxList";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<CheckBoxListPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                checkBoxType: CheckBoxTypeEnum.checkBox,
                horizontal: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _fieldContainerElementRef!: ElementRef;

    // #endregion

    // #region Properties

    public checkBoxType!: Property<CheckBoxTypeEnum>;
    public horizontal!: PropertyBoolean;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<CheckBoxListPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-check-box-list ui form");

        // Trigger Property Changes
        this.checkBoxType.triggerChange();
        this.horizontal.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _checkBoxTypeChanged(change: PropertyChange<CheckBoxTypeEnum>)
    {
        this.refreshListDataSource();
    }
    protected _elementValueChanged()
    {
        if (!this.__processElementValueChanged)
            return;

        this._suspendRefreshFromDataSource();
        this.value.set(this._elementValueGet());
        this._resumeRefreshFromDataSource();
    }
    protected _elementValueGet()
    {
        const
            checkboxes = this.$$element.get()?.find(".checkbox > input:checked"),
            listDataSourceValueProperty = this.listDataSourceValueProperty.get();

        Assert.isNotNullOrUndefined(listDataSourceValueProperty, "listDataSourceValueProperty");

        let
            value: any[] = [];

        if (checkboxes && checkboxes.length)
        {
            checkboxes.each((index, element) =>
            {
                const
                    option = $$(element).parent().dataGet("rad-option");

                if (option)
                    value.push(option[listDataSourceValueProperty]);
            });
        }

        switch (this.checkBoxType.get())
        {
            case CheckBoxTypeEnum.checkBox:
                return value.length ? value : null;
            case CheckBoxTypeEnum.radio:
                return value.length ? value[0] : null;
            default:
                throw new Error("Not implemented.");
        }
    }
    protected _elementValueSet(value: any[] | any)
    {
        this.__processElementValueChanged = false;

        const
            checkboxes = this.$$element.get()?.find("input") as JNative<HTMLInputElement>;

        Assert.isNotNullOrUndefined(checkboxes, "checkboxes");

        checkboxes.parent().removeClass("checked");
        checkboxes.prop("checked", false);

        if (value !== undefined && value !== null)
        {
            switch (this.checkBoxType.get())
            {
                case CheckBoxTypeEnum.checkBox:
                    value.forEach((item: any) =>
                    {
                        checkboxes.filter("[value='" + item + "']").prop("checked", true);
                    });
                    break;
                case CheckBoxTypeEnum.radio:
                    checkboxes.filter("[value='" + value + "']").prop("checked", true);
                    break;
                default:
                    throw new Error("Not implemented.");
            }
        }

        this.__processElementValueChanged = true;
    }
    protected _horizontalChanged(change: PropertyChange<boolean>)
    {
        Assert.isNotNullOrUndefined(this._fieldContainerElementRef, "this._fieldContainerElementRef");

        if (change.currentValue)
        {
            this._fieldContainerElementRef.current.removeClass("grouped");
            this._fieldContainerElementRef.current.addClass("inline");
        }
        else
        {
            this._fieldContainerElementRef.current.addClass("grouped");
            this._fieldContainerElementRef.current.removeClass("inline");
        }
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: CheckBoxListPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._fieldContainerElementRef = new ElementRef();

        this.checkBoxType = new Property<CheckBoxTypeEnum>(this, "System.String, System.Private.CoreLib", propertyValues.checkBoxType, this._checkBoxTypeChanged);
        this.horizontal = new PropertyBoolean({
            context: this,
            initialValue: propertyValues.horizontal,
            changeHandler: this._horizontalChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Horizontal?" }),
            ],
        });

        this.text.overrideGet(next =>
        {
            const
                checkboxes = this.$$element.get()?.find(".checkbox > input:checked"),
                listDataSourceDescriptionProperty = this.listDataSourceDescriptionProperty.get();

            Assert.isNotNullOrUndefined(listDataSourceDescriptionProperty, "listDataSourceDescriptionProperty");

            let
                value = StringHelper.empty;

            if (checkboxes && checkboxes.length > 0)
            {
                checkboxes.each((index, element) =>
                {
                    const
                        option = $$(element).parent().dataGet("rad-option");

                    if (option)
                        value += (value.length ? ", " : StringHelper.empty) + option[listDataSourceDescriptionProperty];
                });
            }

            return value;
        })
        this.value.overrideSet((value, next) =>
        {
            if (ObjectHelper.typeOf(value) === "string")
                value = value.split(",");

            if (value !== undefined && value !== null)
            {
                switch (this.checkBoxType.get())
                {
                    case CheckBoxTypeEnum.checkBox:
                        if (!Array.isArray(value) || value.length === 0)
                            value = null;
                        break;
                    case CheckBoxTypeEnum.radio:
                        break;
                    default:
                        throw new Error("Not implemented.");
                }
            }

            this._elementValueSet(value);
            next(this._elementValueGet());
        });
    }

    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <div>
                <div ref={this._fieldContainerElementRef} class="fields"></div>
            </div>
        );
    }
    protected override _enabledChanged(change: PropertyChange<boolean>)
    {
        super._enabledChanged(change);

        this.$$element.get()?.find(".checkbox").each((index, element) =>
        {
            if (!change.currentValue)
                $$(element).addClass("disabled");
            else
                $$(element).removeClass("disabled");
        });
    }
    protected override _readOnlyChanged(change: PropertyChange<boolean>)
    {
        super._readOnlyChanged(change);

        this.$$element.get()?.find(".checkbox").each((index, element) =>
        {
            if (change.currentValue)
                $$(element).addClass("read-only");
            else
                $$(element).removeClass("read-only");
        });
    }
    protected override _refreshListDataSource()
    {
        super._refreshListDataSource();

        const
            checkBoxType = this.checkBoxType.get(),
            backupValue = this._elementValueGet();

        this._fieldContainerElementRef.current.empty();
        delete this.labelTargetId;

        const
            listDataSource = this.listDataSource.get();

        if (!listDataSource)
            return;

        const
            listData = listDataSource.data.get();

        if (!listData)
            return;

        const
            listDataSourceValueProperty = this.listDataSourceValueProperty.get();

        if (!listDataSourceValueProperty)
            return;

        const
            listDataSourceDescriptionProperty = this.listDataSourceDescriptionProperty.get();

        if (!listDataSourceDescriptionProperty)
            return;

        delete(this.labelTargetId);

        listData
            .forEach((item: any) =>
            {
                const
                    itemId = StringHelper.format("{0}-{1}", this.controlId, item[listDataSourceValueProperty]),
                    inputId = `${itemId}_input`,
                    option =
                        $$(
                            StringHelper.format(
                                "<div class=\"field\">"
                                + "    <div class=\"ui{0}{1} checkbox\" id=\"{2}\">"
                                + "        <input id=\"{8}\" name=\"{4}\" type=\"{3}\" value=\"{5}\">"
                                + "        <label for=\"{8}\"><span>{6}</span>{7}</label>"
                                + "    </div>"
                                + "</div>",
                                checkBoxType === CheckBoxTypeEnum.radio ? " radio" : StringHelper.empty,
                                this.readOnly.get() ? " read-only" : StringHelper.empty,
                                StringHelper.htmlEncode(itemId),
                                checkBoxType?.toLowerCase(),
                                checkBoxType === CheckBoxTypeEnum.radio ? this.controlId : itemId,
                                StringHelper.htmlEncode(item[listDataSourceValueProperty]),
                                StringHelper.htmlEncode(item[listDataSourceDescriptionProperty]),
                                item["imageUrl"]
                                    ? StringHelper.format(
                                        "<img src=\"{0}\" />",
                                        encodeURI(ServiceManager.getRequired(ApplicationInformationService).virtualDirectory + item["imageUrl"])
                                    )
                                    : StringHelper.empty,
                                inputId
                            )
                        ).appendTo(this._fieldContainerElementRef.current);

                option.find(".checkbox").data("rad-option", item);
            });

        FomanticShim(this.$$element.get()?.find(".checkbox")).checkbox({
            onChange: () => { this._elementValueChanged(); }
        });

        this._elementValueSet(backupValue);
    }

    // #endregion
}

